function _construct(Parent, args, Class) {
  if (_isNativeReflectConstruct()) {
    _construct = Reflect.construct;
  } else {
    _construct = function _construct(Parent, args, Class) {
      var a = [null];
      a.push.apply(a, args);
      var Constructor = Function.bind.apply(Parent, a);
      var instance = new Constructor();
      if (Class) _setPrototypeOf(instance, Class.prototype);
      return instance;
    };
  }

  return _construct.apply(null, arguments);
}

function _isNativeReflectConstruct() {
  if (typeof Reflect === "undefined" || !Reflect.construct) return false;
  if (Reflect.construct.sham) return false;
  if (typeof Proxy === "function") return true;

  try {
    Boolean.prototype.valueOf.call(Reflect.construct(Boolean, [], function () {}));
    return true;
  } catch (e) {
    return false;
  }
}

function _setPrototypeOf(o, p) {
  _setPrototypeOf = Object.setPrototypeOf || function _setPrototypeOf(o, p) {
    o.__proto__ = p;
    return o;
  };

  return _setPrototypeOf(o, p);
}

function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}

function _defineProperties(target, props) {
  for (var i = 0; i < props.length; i++) {
    var descriptor = props[i];
    descriptor.enumerable = descriptor.enumerable || false;
    descriptor.configurable = true;
    if ("value" in descriptor) descriptor.writable = true;
    Object.defineProperty(target, descriptor.key, descriptor);
  }
}

function _createClass(Constructor, protoProps, staticProps) {
  if (protoProps) _defineProperties(Constructor.prototype, protoProps);
  if (staticProps) _defineProperties(Constructor, staticProps);
  return Constructor;
}

import invariant from 'invariant';
import { matchPath } from 'react-router';
import pathToRegexp, { compile } from 'path-to-regexp';
import map from 'lodash/map';
import trim from 'lodash/trim';
import flow from 'lodash/flow';
import isNil from 'lodash/isNil';
import partialRight from 'lodash/partialRight';
import isString from 'lodash/isString';
import isEmpty from 'lodash/isEmpty';
import createSymbol from '@signal/utils/createSymbol';
export var PATH = createSymbol('route-path');
export var PARAMS = createSymbol('route-params');
export var isValidPathOrRoute = function isValidPathOrRoute(input) {
  var isValidPath = isString(input) && !isEmpty(trim(input));
  var isRoute = input instanceof Route;
  return isValidPath || isRoute;
};
export var resolvePath = function resolvePath(pathOrRoute) {
  return pathOrRoute instanceof Route ? pathOrRoute.path : pathOrRoute;
}; // Remove leading and trailing whitespace and slashes from the path

export var trimPath = partialRight(trim, ' /');
export var resolvePathSegment = flow([resolvePath, trimPath]);
/**
 * The Route class is a simple and semantic way to represent Routes.
 * @class
 */

export var Route = /*#__PURE__*/function () {
  /**
   * Create a Route.
   * @param {Route|string} ...pathParts - list of paths and/or routes
   */
  function Route() {
    _classCallCheck(this, Route);

    for (var _len = arguments.length, pathParts = new Array(_len), _key = 0; _key < _len; _key++) {
      pathParts[_key] = arguments[_key];
    }

    invariant(pathParts.every(isValidPathOrRoute), '(@signal/router/utils...) createRoute(): Expected args to contain only path strings and/or other Routes');
    this.path = pathParts;
  }
  /**
   * @returns {string}
   */


  _createClass(Route, [{
    key: "toString",
    value: function toString() {
      return this.path;
    }
    /**
     * @returns {string} - path for the route
     */

  }, {
    key: "path",
    get: function get() {
      return this[PATH];
    }
    /**
     * Sets the private path variable and adds a preceding slash. Throws an error
     * if the path has already been set before. It doesn't make sense to support
     * changing the path - Route paths cannot change.
     * @param {string} path
     */
    ,
    set: function set(args) {
      invariant(!this[PATH], // eslint-disable-next-line prettier/prettier
      "(@signal/router/utils...) createRoute(): The Route ".concat(this.path, " already has a path defined. You cannot change the path."));
      var pathSegments = args.map(resolvePathSegment);
      this[PATH] = "/".concat(pathSegments.join('/'));
    }
    /**
     * Fills in the path with the expected route params.
     * @type {Function}
     * @param {object} params - the params to build out the path. Unnecessary params are ignored.
     * @example
     * If the route has a path that looks like this:
     * `/authors/:authorId/books/:bookId`
     *
     * The buildPath call should look like:
     * `route.buildPath({ authorId: 1, bookId: 2 })`
     * @returns {string} the path including params
     */

  }, {
    key: "buildPath",
    value: function buildPath(params) {
      // Create a list of params that should have been passed in but were not.
      var missingParams = this.params.filter(function (param) {
        return isNil(params[param]);
      });
      if (missingParams.length > 0) throw new Error("Insufficent Parameters supplied to `Route.buildPath`. Expected \"".concat(this.params.join(', '), "\" but did not receive \"").concat(missingParams.join(', '), "\"."));
      return compile(this[PATH])(params);
    }
    /**
     * This function finds all of the route's dynamic params and returns an array
     * of their names.
     * @returns {array} Array of the route's parameters
     */

  }, {
    key: "params",
    get: function get() {
      if (this[PARAMS]) return this[PARAMS];
      var keys = [];
      pathToRegexp(this.path, keys);
      this[PARAMS] = map(keys, 'name');
      return this[PARAMS];
    }
    /**
     * Matches a pathname against the Route. If the pathname matches the Route,
     * a react-router `match` object is returned; otherwise, it returns `null`.
     * @see {@link https://reacttraining.com/react-router/web/api/match}
     * @param {string} pathname - path to test
     * @param {Object} [options] - options passed to `matchPath`
     * @param {boolean} [options.exact=false] - when `true`, will only match if
     * the route matches the pathname exactly; otherwise, sub routes will match
     * @param {boolean} [options.strict=false] - enforces trailing slashes
     * @return {Object}
     */

  }, {
    key: "match",
    value: function match(pathname, options) {
      return matchPath(pathname, Object.assign({}, options, {
        path: this.path
      }));
    }
    /**
     * Wraps the `match` method, but instead of returning a `match` object, it
     * returns a boolean.
     * @see {@link Route#match}
     * @param {*} ...args - args passed to `match`
     * @return {boolean}
     */

  }, {
    key: "test",
    value: function test() {
      return Boolean(this.match.apply(this, arguments));
    }
  }]);

  return Route;
}();
/**
 * Instantiates a new Route.
 * @param {Route|string} parentRouteOrPath - either a parent route or the path
 * @param {string} [path] - if parent route is provided, this should be path
 * @returns Instance of Route
 */

export default function createRoute() {
  for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
    args[_key2] = arguments[_key2];
  }

  return _construct(Route, args);
}